import { Download, Expand, Upload } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon, label, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-50"
          aria-label={label}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface ArtifactToolbarActionsProps {
  /** Called when the download button is clicked. */
  onDownload?: () => void;
  /** Called when the export-to-workbench button is clicked. */
  onExport?: () => void;
  /** Called when the fullscreen button is clicked. */
  onFullscreen?: () => void;
}

/**
 * Shared toolbar action buttons for artifact message components.
 * Renders download, export, and fullscreen buttons with
 * consistent styling and layout.
 */
export function ArtifactToolbarActions({
  onDownload,
  onExport,
  onFullscreen,
}: ArtifactToolbarActionsProps) {
  return (
    <div className="ml-auto flex items-center">
      {onDownload && (
        <ToolbarButton
          icon={<Download className="size-3.5" />}
          label="Download"
          onClick={onDownload}
        />
      )}
      {onExport && (
        <ToolbarButton
          icon={<Upload className="size-3.5" />}
          label="Export to workbench"
          onClick={onExport}
        />
      )}
      {onFullscreen && (
        <ToolbarButton
          icon={<Expand className="size-3.5" />}
          label="View in full screen"
          onClick={onFullscreen}
        />
      )}
    </div>
  );
}
